import { Box, Chip, Divider, Paper, Typography } from "@mui/material";
import {
    KboFlowRunOutput,
    KboPhaseRecord,
} from "../KboFlowDebug/KboFlowRunDebug";
import { formatDuration, formatNumber } from "../KboFlowDebug/KboFlowDebugUI";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";

/** Sticky phase list beside the run, scroll-spied against the whole-run cards. */

const PHASE_DISPLAY: Record<string, { title: string; cardId: string }> = {
    contextassembly: { title: "Context assembly", cardId: "phase-1" },
    questiongeneration: { title: "Question generation", cardId: "phase-2" },
    questionvalidation: { title: "Question validation", cardId: "phase-3" },
    multilevelanalysis: { title: "Multi-level analysis", cardId: "phase-4" },
    analysis: { title: "Multi-level analysis", cardId: "phase-4" },
    multilevelreportassembly: { title: "Report assembly", cardId: "phase-5" },
    reportassembly: { title: "Report assembly", cardId: "phase-5" },
    commit: { title: "Commit", cardId: "phase-5" },
    multilevelanalysiscommit: { title: "Commit", cardId: "phase-5" },
};

function phaseDisplayInfo(
    phase: string | undefined,
    index: number,
): { title: string; cardId: string } {
    const key = phase?.toLowerCase().replace(/[^a-z]/g, "");
    return (
        (key ? PHASE_DISPLAY[key] : undefined) ?? {
            title: phase ?? "Phase",
            cardId: `phase-${index + 1}`,
        }
    );
}

function scrollToPhaseCard(cardId: string) {
    document
        .getElementById(cardId)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

// The analysis, report, and commit phases are rendered on the per-question tabs,
// so this list covers only the cards that exist on the whole-run tab.
const WHOLE_RUN_CARD_IDS = new Set(["phase-1", "phase-2", "phase-3"]);

export function PhaseTimeline({
    output,
    phases,
    activeCardId,
}: {
    output: KboFlowRunOutput;
    phases: KboPhaseRecord[];
    activeCardId?: string;
}) {
    const invocations = phases.flatMap((phase) => phase.invocations ?? []);
    const totalTokens = invocations.reduce(
        (total, call) =>
            total +
            (call.totalTokens ??
                (call.inputTokens ?? 0) + (call.outputTokens ?? 0)),
        0,
    );
    const model = invocations.find((call) => call.model)?.model;
    const failedCalls = invocations.filter(
        (call) => call.error || call.status?.toLowerCase() === "failed",
    ).length;
    const phaseDurations = phases.map((phase) =>
        phase.startedAtUtc && phase.completedAtUtc
            ? new Date(phase.completedAtUtc).getTime() -
              new Date(phase.startedAtUtc).getTime()
            : undefined,
    );
    const longestDuration = Math.max(
        ...phaseDurations.filter((ms): ms is number => ms !== undefined),
        0,
    );
    const listedPhases = phases
        .map((phase, index) => ({
            phase,
            index,
            ...phaseDisplayInfo(phase.phase, index),
        }))
        .filter((entry) => WHOLE_RUN_CARD_IDS.has(entry.cardId));

    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                maxHeight: "calc(100vh - 32px)",
                overflowY: "auto",
                p: 2.25,
                position: "sticky",
                top: 16,
            }}
        >
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 1,
                    mb: 1.5,
                }}
            >
                <Typography sx={{ fontWeight: 750 }}>Phases</Typography>
                <Chip
                    size="small"
                    label={listedPhases.length}
                    sx={{ ml: "auto" }}
                />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {listedPhases.map(({ phase, index, title, cardId }, position) => {
                    const isAgent = (phase.invocations?.length ?? 0) > 0;
                    const isLongest =
                        longestDuration > 0 &&
                        phaseDurations[index] === longestDuration;
                    const isActive = cardId === activeCardId;
                    return (
                        <Box
                            key={`${phase.phase}-${index}`}
                            component="button"
                            type="button"
                            onClick={() => scrollToPhaseCard(cardId)}
                            sx={{
                                bgcolor: isActive
                                    ? pageColors.blueSoft
                                    : "#F8FAFF",
                                border: "1px solid",
                                borderColor: isActive
                                    ? pageColors.blue
                                    : "divider",
                                borderRadius: 1.5,
                                cursor: "pointer",
                                font: "inherit",
                                p: 1.25,
                                textAlign: "left",
                                transition:
                                    "background-color 120ms, border-color 120ms",
                                width: "100%",
                            }}
                        >
                            <Box
                                sx={{
                                    alignItems: "center",
                                    display: "flex",
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: isActive
                                            ? pageColors.blue
                                            : pageColors.ink,
                                        flex: 1,
                                        fontWeight: 700,
                                    }}
                                >
                                    {position + 1} · {title}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={isAgent ? "AGENT" : "RULES"}
                                    sx={{
                                        bgcolor: isAgent
                                            ? pageColors.blueSoft
                                            : "#F2F4F7",
                                        color: isAgent
                                            ? pageColors.blue
                                            : pageColors.muted,
                                        fontWeight: 800,
                                        fontSize: 11,
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.25 }}
                            >
                                {formatDuration(
                                    phase.startedAtUtc,
                                    phase.completedAtUtc,
                                )}
                                {" · "}
                                {phase.invocations?.length ?? 0} model calls
                                {isLongest ? " · longest phase" : ""}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            <Divider sx={{ borderStyle: "dashed", my: 1.5 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                        Model
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {model ?? "—"}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                        Model calls
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {invocations.length}
                        {failedCalls > 0
                            ? ` · ${failedCalls} failed`
                            : " · all succeeded"}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                        Tokens
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatNumber(totalTokens)}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                        Wall clock
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatDuration(
                            output.startedAtUtc,
                            output.completedAtUtc,
                        )}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
}
